import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import { Search, ArrowBackIos, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { product } from "../../api/paquetes";
import { useNotification } from "../../context/notification.context";
import dayjs from "dayjs";

interface PagoRealizado {
  id: string;
  monto: number;
  vendedor: string;
  fecha: string;
  concepto: string;
  cargador: string;
  paqueteId: number;
  tc: number;
}

const TAG_PAGO_VENDEDOR = "[PAGO_VENDEDOR]";

const PagosVendedores: React.FC = () => {
  const [terminoBusqueda, setTerminoBusqueda] = useState("");
  const [pagosData, setPagosData] = useState<PagoRealizado[]>([]);
  const [pagina, setPagina] = useState(1);
  const [cargando, setCargando] = useState(true);
  const { getError, getSuccess } = useNotification();
  const navigate = useNavigate();

  // --- RBAC: Solo Superadministrador y Administrador pueden acceder ---
  const userData = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); }
    catch { return {}; }
  }, []);
  const isAdmin = userData.rol === "Superadministrador" || userData.rol === "Administrador";

  React.useEffect(() => {
    if (!isAdmin) {
      navigate("/", { replace: true });
    }
  }, [isAdmin, navigate]);

  const ITEMS_PER_PAGE = 10;
  const PURPLE_LIGHT = "#9c27b0";
  const PURPLE_HOVER = "#7b1fa2";

  const cargarDatos = useCallback(async () => {
    try {
      setCargando(true);
      const response = await product.getAll();
      const paquetesAPI = response.data?.data || response.data || [];

      // Filtrar solo paquetes individuales
      const individuales = paquetesAPI.filter((p: any) => {
        const hasGrupalTag = p.descripcion && typeof p.descripcion === 'string' && p.descripcion.includes('[GRUPAL:');
        const hasGrupalObs = p.observaciones && typeof p.observaciones === 'string' && p.observaciones.includes('METADATA_GRUPAL');
        return !p.is_grupal && !hasGrupalTag && !hasGrupalObs;
      });

      const todosLosPagos: PagoRealizado[] = [];

      individuales.forEach((p: any) => {
        const vendedorNombre = (p.vendedor_nombre || p.vendedor || "SISTEMA").trim();
        const pagos = p.pagosPaquetes || [];

        pagos.forEach((pago: any) => {
          // SOLO MOSTRAR SI TIENE EL TAG DE PAGO A VENDEDOR
          if (!pago.deleted_at && pago.observacion?.includes(TAG_PAGO_VENDEDOR)) {
            const importe = parseFloat(pago.importe) || 0;
            const tc = parseFloat(pago.tipoCambio || pago.tipo_cambio || pago.tc || "1");
            const montoFinal = tc > 1 ? (importe * tc) : importe;

            todosLosPagos.push({
              id: pago.id || Math.random().toString(),
              vendedor: vendedorNombre,
              fecha: pago.created_at || "",
              concepto: (pago.observacion || "").replace(TAG_PAGO_VENDEDOR, "").trim() || "-",
              cargador: pago.responsable_nombre || "SISTEMA",
              paqueteId: p.id,
              tc: tc,
              monto: importe,
            });
          }
        });
      });

      // Ordenar por fecha descendente
      todosLosPagos.sort((a, b) => dayjs(b.fecha).unix() - dayjs(a.fecha).unix());

      setPagosData(todosLosPagos);
    } catch (error) {
      getError("Error al cargar los pagos realizados");
    } finally {
      setCargando(false);
    }
  }, [getError]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleEliminarPago = async (paqueteId: number, pagoId: any) => {
    if (!window.confirm("¿Está seguro que desea eliminar este pago?")) return;
    try {
      await product.deletePago(paqueteId, pagoId);
      getSuccess("Pago eliminado correctamente");
      cargarDatos();
    } catch (error) {
      getError("Error al eliminar el pago");
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTerminoBusqueda(event.target.value);
    setPagina(1);
  };

  const datosFiltrados = pagosData.filter((p) =>
    p.vendedor.toLowerCase().includes(terminoBusqueda.toLowerCase()) ||
    p.concepto.toLowerCase().includes(terminoBusqueda.toLowerCase()) ||
    p.cargador.toLowerCase().includes(terminoBusqueda.toLowerCase())
  );

  const totalPaginas = Math.ceil(datosFiltrados.length / ITEMS_PER_PAGE);
  const datosPaginados = datosFiltrados.slice(
    (pagina - 1) * ITEMS_PER_PAGE,
    pagina * ITEMS_PER_PAGE
  );

  return (
    <Box sx={{ p: 3, maxWidth: "1600px", margin: "0 auto" }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: "flex", alignItems: "center" }}>
        <IconButton onClick={() => navigate("/paquetes", { state: { tab: 3 } })} sx={{ mr: 2 }}>
          <ArrowBackIos fontSize="small" />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: "normal", color: "#666" }}>
          Pagos realizados a vendedores
        </Typography>
      </Box>

      {/* Barra de Búsqueda */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <TextField
          size="small"
          placeholder="Buscar en la tabla"
          value={terminoBusqueda}
          onChange={handleSearchChange}
          sx={{
            width: "350px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "4px",
              backgroundColor: "#fff"
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search fontSize="small" sx={{ color: "action.active" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Tabla */}
      {cargando ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 10 }}>
          <CircularProgress sx={{ color: "#461264" }} />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #eee", borderRadius: "4px" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ "& th": { borderBottom: "2px solid #eee", fontWeight: "bold", color: "#461264" } }}>
                <TableCell>Monto</TableCell>
                <TableCell>Vendedor</TableCell>
                <TableCell>Fecha de pago</TableCell>
                <TableCell>Concepto</TableCell>
                <TableCell>Cargador</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {datosPaginados.map((row) => (
                <TableRow key={row.id} sx={{ "&:hover": { backgroundColor: "#f9f9f9" }, "& td": { py: 1.5, borderBottom: "1px solid #efefef" } }}>
                  <TableCell sx={{ color: "#444" }}>
                    {row.tc > 1 ? "U$S" : "$"} {row.monto.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell sx={{ color: "#444" }}>{row.vendedor}</TableCell>
                  <TableCell sx={{ color: "#444" }}>
                    {row.fecha ? dayjs(row.fecha).format("DD/MM/YYYY") : "-"}
                  </TableCell>
                  <TableCell sx={{ color: "#444" }}>{row.concepto}</TableCell>
                  <TableCell sx={{ color: "#444" }}>{row.cargador}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleEliminarPago(row.paqueteId, row.id)}
                      title="Eliminar Pago"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {datosPaginados.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: "#999" }}>
                    No se encontraron pagos
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Paginación Funcional */}
      {totalPaginas > 1 && (
        <Box sx={{ display: "flex", mt: 3, alignItems: "center" }}>
          <Box sx={{ display: "flex", border: "1px solid #dee2e6", borderRadius: "4px", overflow: "hidden" }}>
            <Button
              disabled={pagina === 1}
              onClick={() => setPagina(p => p - 1)}
              sx={{
                color: pagina === 1 ? "#6c757d" : PURPLE_LIGHT,
                textTransform: "none",
                px: 2,
                py: 1,
                fontSize: "0.9rem",
                backgroundColor: "#fff",
                borderRight: "1px solid #dee2e6",
                borderRadius: 0,
                minWidth: "auto",
                "&:hover": { backgroundColor: "#f8f9fa" }
              }}
            >
              ← Anterior
            </Button>

            {[...Array(totalPaginas)].map((_, i) => (
              <Button
                key={i + 1}
                onClick={() => setPagina(i + 1)}
                sx={{
                  backgroundColor: pagina === i + 1 ? PURPLE_LIGHT : "#fff",
                  color: pagina === i + 1 ? "#fff" : PURPLE_LIGHT,
                  minWidth: "40px",
                  borderRadius: 0,
                  boxShadow: "none",
                  borderRight: i + 1 === totalPaginas ? "none" : "1px solid #dee2e6",
                  "&:hover": { backgroundColor: pagina === i + 1 ? PURPLE_HOVER : "#f8f9fa" }
                }}
              >
                {i + 1}
              </Button>
            ))}

            <Button
              disabled={pagina === totalPaginas}
              onClick={() => setPagina(p => p + 1)}
              sx={{
                color: pagina === totalPaginas ? "#6c757d" : PURPLE_LIGHT,
                textTransform: "none",
                px: 2,
                py: 1,
                fontSize: "0.9rem",
                borderLeft: "1px solid #dee2e6",
                borderRadius: 0,
                "&:hover": { backgroundColor: "#f8f9fa" }
              }}
            >
              Siguiente →
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PagosVendedores;
